import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from '@mui/material';
import type { TFunction } from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import { getSaveSlotDisplayProgramName, isSaveSlotEmpty, type SaveSlotLike } from '../../../utils/saveSlotProjectId';

interface MoveSlotOverwriteConfirmDialogProps {
  destinationSlot: SaveSlotLike | null | undefined;
  destinationSlotNumber: number | null;
  confirmDisabled?: boolean;
  isMoving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  open: boolean;
}

function getDestinationProgramName(destinationSlot: SaveSlotLike | null | undefined, slotNumber: number, t: TFunction): string {
  const fallbackSlotName = t('moveSlotOverwriteDialog.destinationSlotName', 'slot {{slotNumber}}', { slotNumber });

  if (isSaveSlotEmpty(destinationSlot)) {
    return fallbackSlotName;
  }

  return getSaveSlotDisplayProgramName(destinationSlot?.programTitle) || fallbackSlotName;
}

const MoveSlotOverwriteConfirmDialog = ({
  destinationSlot,
  destinationSlotNumber,
  confirmDisabled = false,
  isMoving,
  onCancel,
  onConfirm,
  open,
}: MoveSlotOverwriteConfirmDialogProps) => {
  const { t } = useTranslation();
  const slotNumber = destinationSlotNumber;
  const destinationProgramName =
    slotNumber === null ? t('moveSlotOverwriteDialog.destinationUnknown', 'this slot') : getDestinationProgramName(destinationSlot, slotNumber, t);

  return (
    <Dialog open={open} onClose={isMoving ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle variant="dialogTitle" align="center">
        <Trans i18nKey="moveSlotOverwriteDialog.title">Program already present in this slot</Trans>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ py: 3, px: 3 }}>
        <Typography variant="body1" component="p">
          {t(
            'moveSlotOverwriteDialog.body',
            'This will replace the program currently saved in {{destinationProgramName}}. Do you want to continue?',
            { destinationProgramName }
          )}
        </Typography>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'center', px: 2, py: 2 }}>
        <Stack direction="row" spacing={2}>
          <Button variant="text" onClick={onCancel} disabled={isMoving}>
            <Trans i18nKey="moveSlotOverwriteDialog.cancel">Cancel</Trans>
          </Button>
          <Button variant="contained" color="error" onClick={onConfirm} disabled={isMoving || confirmDisabled}>
            <Trans i18nKey="moveSlotOverwriteDialog.confirm">Confirm</Trans>
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default MoveSlotOverwriteConfirmDialog;
